/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Input, Modal, message } from 'antd';
import { DownOutlined, FileOutlined } from '@ant-design/icons';
import FinderJs from '@/utils/finderJS';

/** Map kết quả Finder (openFileManager) → danh sách URL/path lưu được. */
export function mapFinderSelectionToFileUrls(selectedFiles: any[] | null | undefined): string[] {
  if (!selectedFiles?.length) return [];
  return selectedFiles
    .map((file: any) => {
      const direct = String(file?.fileUrl || '').trim();
      if (direct) return direct;
      return `${file?.baseUrl || ''}${file?.viewUrl || ''}`;
    })
    .filter((u: string) => Boolean(String(u).trim()));
}

export interface FileAndLinkUploadMenuProps {
  /** Parent gộp file, gọi API, v.v. Có thể sync hoặc async. */
  onAppendUrls: (urls: string[]) => void | Promise<void>;
  disabled?: boolean;
  block?: boolean;
  /** Chỉ hiển thị icon, không hiển thị text Upload. */
  iconOnly?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** z-index modal chèn URL (khi dùng trong Modal cha, tăng nếu bị che). */
  urlModalZIndex?: number;
  /** Icon hiển thị bên ngoài button. */
  icon?: React.ReactNode;
}

/**
 * Menu Upload: chọn file qua Finder hoặc chèn URL (một hoặc nhiều link, phân tách bằng , ; xuống dòng).
 * Dùng chung Báo cáo KPI, modal tài liệu công việc, v.v.
 */
export const FileAndLinkUploadMenu: React.FC<FileAndLinkUploadMenuProps> = ({
  onAppendUrls,
  disabled = false,
  block = false,
  iconOnly = false,
  className,
  style,
  urlModalZIndex = 1100,
  icon = <FileOutlined />,
}) => {
  const { t } = useTranslation();
  const [urlModalOpen, setUrlModalOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlSaving, setUrlSaving] = useState(false);

  const runAppend = useCallback(
    async (urls: string[]) => {
      const cleaned = urls.map((u) => String(u).trim()).filter(Boolean);
      if (cleaned.length === 0) return;
      await Promise.resolve(onAppendUrls(cleaned));
    },
    [onAppendUrls]
  );

  const openFinder = useCallback(() => {
    FinderJs.openFileManager({ multiple: true, allowedTypes: 'all' }, async (selectedFiles: any[] | null) => {
      if (selectedFiles == null || selectedFiles.length === 0) return;
      try {
        console.log('selectedFiles', selectedFiles);
        const newFiles = mapFinderSelectionToFileUrls(selectedFiles);
        if (newFiles.length === 0) return;
        await runAppend(newFiles);
      } catch (err) {
        console.error('FileAndLinkUploadMenu: finder append failed', err);
        message.error(t('bsc.msg.uploadthatbai', 'Upload file thất bại!'));
      }
    });
  }, [runAppend, t]);

  const handleUrlOk = useCallback(async () => {
    const raw = urlInput.trim();
    if (!raw) {
      message.warning(t('bsc.title.nhapurl', 'Vui lòng nhập URL'));
      return;
    }
    const parts = raw
      .split(/[,\n;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) {
      message.warning(t('bsc.title.nhapurl', 'Vui lòng nhập URL'));
      return;
    }
    try {
      setUrlSaving(true);
      await runAppend(parts);
      setUrlModalOpen(false);
      setUrlInput('');
      message.success(t('bsc.msg.dachenurl', 'Đã chèn URL'));
    } catch (err) {
      console.error('FileAndLinkUploadMenu: URL append failed', err);
      message.error(t('bsc.msg.luuurlthatbai', 'Lưu URL thất bại'));
    } finally {
      setUrlSaving(false);
    }
  }, [runAppend, urlInput, t]);

  return (
    <>
      <div onClick={(e) => e.stopPropagation()} className={className} style={style}>
        <Dropdown
          disabled={disabled}
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'file',
                label: t('bsc.btn.uploadfile_menu', 'Upload file...'),
                onClick: ({ domEvent }) => {
                  domEvent.stopPropagation();
                  openFinder();
                },
              },
              {
                key: 'url',
                label: t('bsc.title.chenurl', 'Chèn URL'),
                onClick: ({ domEvent }) => {
                  domEvent.stopPropagation();
                  setUrlInput('');
                  setUrlModalOpen(true);
                },
              },
            ],
          }}
        >
          <Button
            block={block}
            type="default"
            disabled={disabled}
            icon={icon}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: iconOnly ? 0 : 6,
              minWidth: iconOnly ? 32 : undefined,
              width: iconOnly ? 32 : undefined,
              paddingInline: iconOnly ? 0 : undefined,
            }}
          >
            {!iconOnly && (
              <>
                {t('bsc.btn.upload_label', 'Upload')}
                <DownOutlined style={{ fontSize: 10 }} />
              </>
            )}
          </Button>
        </Dropdown>
      </div>

      <Modal
        title={t('bsc.title.chenurl', 'Chèn URL')}
        open={urlModalOpen}
        okText={t('bsc.btn.luu', 'Lưu')}
        cancelText={t('bsc.btn.huy', 'Hủy')}
        onOk={() => void handleUrlOk()}
        onCancel={() => {
          setUrlModalOpen(false);
          setUrlInput('');
        }}
        confirmLoading={urlSaving}
        destroyOnClose
        zIndex={urlModalZIndex}
      >
        <Input.TextArea
          placeholder="https://... (nhiều link: phẩy, xuống dòng hoặc ;)"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          autoSize={{ minRows: 3, maxRows: 8 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              void handleUrlOk();
            }
          }}
          allowClear
        />
      </Modal>
    </>
  );
};
